import { useCallback, useEffect, useState } from 'react'
import { supabase } from '../lib/supabase'
import type { Nalog, Uloga } from '../lib/tipovi'

const ULOGE: { vrijednost: Uloga; oznaka: string }[] = [
  { vrijednost: 'magacioner', oznaka: 'Magacioner' },
  { vrijednost: 'vozac', oznaka: 'Vozač' },
  { vrijednost: 'citalac', oznaka: 'Čitalac' },
  { vrijednost: 'knjigovodja', oznaka: 'Knjigovođa' },
  { vrijednost: 'admin', oznaka: 'Admin' },
]

const nazivUloge = (u: string) => ULOGE.find((x) => x.vrijednost === u)?.oznaka ?? u

type Clan = {
  korisnik_id: string
  uloga: Uloga
  dodano: string
  ime: string
}

type Ishod = {
  email: string
  nacin: string
  vec_postojao: boolean
  ranija_uloga: string | null
  privremena_lozinka?: string
}

const datum = new Intl.DateTimeFormat('sr-Latn-ME', {
  day: '2-digit',
  month: '2-digit',
  year: 'numeric',
})

/** Korisnici firme i pozivanje novih (C25).
 *
 *  Ekran ne pravi nalog sam: zove Edge funkciju `pozivnica`, koja pod JWT-om pozivaoca
 *  prvo pita bazu (`provjeri_admina()`). Zato ovdje nema nijedne provjere na koju bi se
 *  smjelo osloniti — sakriven obrazac je udobnost, a ne zaštita.
 */
export function Korisnici({ nalog }: { nalog: Nalog | null }) {
  const [clanovi, postaviClanove] = useState<Clan[]>([])
  const [ucitava, postaviUcitava] = useState(true)
  const [greskaListe, postaviGreskuListe] = useState<string | null>(null)

  const [ime, postaviIme] = useState('')
  const [uloga, postaviUlogu] = useState<Uloga>('magacioner')
  const [bezEmaila, postaviBezEmaila] = useState(false)
  const [adresa, postaviAdresu] = useState('')
  const [salje, postaviSalje] = useState(false)
  const [greska, postaviGresku] = useState<string | null>(null)
  const [ishod, postaviIshod] = useState<Ishod | null>(null)

  const jeAdmin = nalog?.uloga === 'admin'

  /** Čim admin krene da kuca sljedećeg, raniji ishod silazi sa ekrana: lozinka koja stoji
   *  uz tuđe ime je gora od nikakve. */
  function ocisti() {
    if (ishod) postaviIshod(null)
    if (greska) postaviGresku(null)
  }

  const ucitaj = useCallback(async () => {
    postaviUcitava(true)
    const { data, error } = await supabase
      .from('clanstva')
      .select('korisnik_id, uloga, dodano, profili:korisnik_id (ime)')

    if (error) {
      postaviGreskuListe(error.message)
      postaviUcitava(false)
      return
    }

    const redovi = (data ?? []).map((red) => {
      const r = red as unknown as {
        korisnik_id: string
        uloga: Uloga
        dodano: string
        profili: { ime: string } | { ime: string }[] | null
      }
      const profil = Array.isArray(r.profili) ? r.profili[0] : r.profili
      return {
        korisnik_id: r.korisnik_id,
        uloga: r.uloga,
        dodano: r.dodano,
        ime: profil?.ime ?? '—',
      }
    })

    redovi.sort((a, b) => a.ime.localeCompare(b.ime, 'sr-Latn-ME'))
    postaviClanove(redovi)
    postaviGreskuListe(null)
    postaviUcitava(false)
  }, [])

  useEffect(() => {
    void ucitaj()
  }, [ucitaj])

  async function posalji(dogadjaj: React.FormEvent) {
    dogadjaj.preventDefault()
    postaviGresku(null)
    postaviIshod(null)
    postaviSalje(true)

    const { data, error } = await supabase.functions.invoke('pozivnica', {
      body: {
        ime: ime.trim(),
        uloga,
        ...(bezEmaila ? { nalog: adresa.trim() } : { email: adresa.trim() }),
      },
    })

    postaviSalje(false)

    if (error) {
      // Funkcija vraća { kod, poruka }; supabase-js to ostavlja u odgovoru, ne u poruci.
      let poruka = error.message
      const kontekst = (error as { context?: Response }).context
      if (kontekst && typeof kontekst.json === 'function') {
        try {
          const tijelo = await kontekst.json()
          if (tijelo?.poruka) poruka = `${tijelo.poruka}${tijelo.kod ? ` (${tijelo.kod})` : ''}`
        } catch {
          // ostaje poruka iz same greške
        }
      }
      postaviGresku(poruka)
      return
    }

    postaviIshod(data as Ishod)
    postaviIme('')
    postaviAdresu('')
    void ucitaj()
  }

  const mozeDalje = ime.trim() !== '' && adresa.trim() !== '' && !salje

  return (
    <>
      {greskaListe && (
        <div className="greska" role="alert" data-testid="korisnici-greska">
          {greskaListe}
        </div>
      )}

      {ucitava ? (
        <div className="ucitavanje" data-testid="korisnici-ucitavanje">
          Učitavanje…
        </div>
      ) : (
        <div className="tabela-okvir">
          <table className="tabela" data-testid="korisnici-tabela">
            <thead>
              <tr>
                <th scope="col">Ime</th>
                <th scope="col">Uloga</th>
                <th scope="col">U firmi od</th>
              </tr>
            </thead>
            <tbody>
              {clanovi.map((clan) => (
                <tr key={clan.korisnik_id} data-testid="korisnici-red" data-uloga={clan.uloga}>
                  <td className="tabela__naziv">
                    {clan.ime}
                    {clan.korisnik_id === nalog?.korisnikId && (
                      <span className="tabela__tiho"> · ti</span>
                    )}
                  </td>
                  <td>{nazivUloge(clan.uloga)}</td>
                  <td className="tabela__broj">{datum.format(new Date(clan.dodano))}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}

      <div className="odjeljak">
        <h2 className="oznaka odjeljak__naslov">Novi korisnik</h2>

        {!jeAdmin ? (
          <p className="razlog" data-testid="korisnici-samo-admin">
            Korisnike dodaje admin firme (C25).
          </p>
        ) : (
          <form className="obrazac" onSubmit={posalji} data-testid="pozivnica-forma">
            <div className="obrazac__polje">
              <label className="oznaka polje-oznaka" htmlFor="polje-ime">
                Ime i prezime
              </label>
              <input
                id="polje-ime"
                className="polje"
                value={ime}
                onChange={(e) => { ocisti(); postaviIme(e.target.value) }}
                data-testid="polje-ime"
              />
            </div>

            <div className="obrazac__polje">
              <label className="oznaka polje-oznaka" htmlFor="polje-uloga">
                Uloga
              </label>
              <select
                id="polje-uloga"
                className="polje"
                value={uloga}
                onChange={(e) => postaviUlogu(e.target.value as Uloga)}
                data-testid="polje-uloga"
              >
                {ULOGE.map((u) => (
                  <option key={u.vrijednost} value={u.vrijednost}>
                    {u.oznaka}
                  </option>
                ))}
              </select>
            </div>

            <div className="obrazac__polje">
              <label className="oznaka polje-oznaka" htmlFor="polje-nacin">
                Način
              </label>
              <select
                id="polje-nacin"
                className="polje"
                value={bezEmaila ? 'nalog' : 'email'}
                onChange={(e) => {
                  ocisti()
                  postaviBezEmaila(e.target.value === 'nalog')
                  postaviAdresu('')
                }}
                data-testid="polje-nacin"
              >
                <option value="email">Pozivnica e-poštom</option>
                <option value="nalog">Radnik bez e-pošte</option>
              </select>
            </div>

            <div className="obrazac__polje">
              <label className="oznaka polje-oznaka" htmlFor="polje-adresa">
                {bezEmaila ? 'Korisničko ime' : 'E-pošta'}
              </label>
              <input
                id="polje-adresa"
                className="polje"
                type={bezEmaila ? 'text' : 'email'}
                autoCapitalize="none"
                spellCheck={false}
                placeholder={bezEmaila ? 'petar.v' : 'petar@primjer.me'}
                value={adresa}
                onChange={(e) => { ocisti(); postaviAdresu(e.target.value) }}
                data-testid="polje-adresa"
              />
            </div>

            <div className="obrazac__dugme">
              <button
                type="submit"
                className="dugme dugme--glavno"
                disabled={!mozeDalje}
                data-testid="dugme-posalji"
              >
                {salje ? 'Šaljem…' : bezEmaila ? 'Napravi nalog' : 'Pošalji pozivnicu'}
              </button>
              {!mozeDalje && !salje && (
                <div className="razlog">
                  Unesi ime i {bezEmaila ? 'korisničko ime' : 'e-poštu'}.
                </div>
              )}
            </div>
          </form>
        )}

        {greska && (
          <div className="greska" role="alert" data-testid="pozivnica-greska">
            {greska}
          </div>
        )}

        {ishod && (
          <div className="ishod" role="status" data-testid="pozivnica-ishod">
            <div className="ishod__red">
              <span className="oznaka">Nalog</span>
              <span className="mono-kod" data-testid="ishod-email">
                {ishod.email}
              </span>
            </div>
            <div className="ishod__red">
              <span className="oznaka">Način</span>
              <span>
                {ishod.nacin === 'nalog_bez_emaila'
                  ? 'Nalog bez e-pošte'
                  : 'Pozivnica poslata e-poštom'}
                {ishod.vec_postojao && ' · korisnik je već postojao'}
                {ishod.ranija_uloga && ` · ranije ${nazivUloge(ishod.ranija_uloga)}`}
              </span>
            </div>
            {ishod.privremena_lozinka && (
              <>
                <div className="ishod__red">
                  <span className="oznaka">Privremena lozinka</span>
                  <span className="mono-kod" data-testid="ishod-lozinka">
                    {ishod.privremena_lozinka}
                  </span>
                </div>
                <p className="razlog">
                  Ispisuje se samo sada — predaj je radniku. Aplikacija će tražiti novu
                  lozinku pri prvoj prijavi.
                </p>
              </>
            )}
          </div>
        )}
      </div>
    </>
  )
}
